import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getCotizaciones,
  getStats,
  editarCotizacion,
  marcarAtendida,
  exportarExcel,
  getLaboratorios,
} from '../api/pricing.api'
import type { CotizacionFilters, EditarCotizacionPayload, MarcarAtendidaPayload } from '../types'

const PRICING_KEY = ['pricing'] as const

export const pricingKeys = {
  all: PRICING_KEY,
  cotizaciones: (filters?: object) => [...PRICING_KEY, 'cotizaciones', filters] as const,
  stats: (filters?: object) => [...PRICING_KEY, 'stats', filters] as const,
  laboratorios: [...PRICING_KEY, 'laboratorios'] as const,
}

export function useCotizaciones(filters: CotizacionFilters = {}) {
  return useQuery({
    queryKey: pricingKeys.cotizaciones(filters),
    queryFn: () => getCotizaciones(filters),
  })
}

export function usePricingStats(filters: Omit<CotizacionFilters, 'page' | 'page_size' | 'search'> = {}) {
  return useQuery({
    queryKey: pricingKeys.stats(filters),
    queryFn: () => getStats(filters),
  })
}

export function useLaboratorios() {
  return useQuery({
    queryKey: pricingKeys.laboratorios,
    queryFn: () => getLaboratorios(),
    staleTime: 5 * 60 * 1000,
  })
}

export function useEditarCotizacion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: EditarCotizacionPayload }) =>
      editarCotizacion(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.all })
      toast.success('Cotización actualizada correctamente')
    },
    onError: () => toast.error('Error al actualizar cotización'),
  })
}

export function useMarcarAtendida() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: MarcarAtendidaPayload }) =>
      marcarAtendida(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingKeys.all })
      toast.success('Estado de cotización actualizado')
    },
    onError: () => toast.error('Error al actualizar estado'),
  })
}

export function useExportarExcel() {
  return useMutation({
    mutationFn: (filters: Omit<CotizacionFilters, 'page' | 'page_size' | 'search'>) =>
      exportarExcel(filters),
    onSuccess: () => toast.success('Archivo descargado'),
    onError: () => toast.error('Error al exportar'),
  })
}
